import { LanguageChangeSource, useStatsigTracking } from "@configs/statsig";
import { useLanguages } from "@hooks/useLanguages";
import { Language } from "@types";
import { DrawerClasses, LanguageSelector, LanguageSelectorClasses, LanguageValue, cn } from "kz-ui-sdk";
import { useTranslation } from "react-i18next";

interface LanguageSelectProps {
  onLanguageChange?: (language: Language) => void;
  className?: string;
  classes?: LanguageSelectorClasses["classes"];
  title?: string;
  closeOnSelect?: boolean;
  drawerClasses?: DrawerClasses["classes"];
  hiddenComponent?: React.ReactNode;
}

const LanguageSelect = ({
  onLanguageChange,
  className,
  classes,
  title,
  closeOnSelect,
  drawerClasses,
  hiddenComponent = <span></span>,
}: LanguageSelectProps) => {
  const { t } = useTranslation();
  const { currentLanguage, languages, changeLanguage, isMultiLanguage } = useLanguages();
  const { trackChangeLanguage } = useStatsigTracking();

  const handleOnChangeLanguage = (option: LanguageValue) => {
    const from = currentLanguage.code as string;
    const to = option.code as string;
    if (from !== to) {
      trackChangeLanguage(from, to, LanguageChangeSource.LANGUAGE_SELECT);
    }
    changeLanguage(option.code as Language);
    onLanguageChange?.(option.code as Language);
  };

  if (!isMultiLanguage) return hiddenComponent;

  return (
    <LanguageSelector
      languages={languages}
      defaultValue={languages.find((lang) => lang.code === currentLanguage.code)}
      variant="ghost"
      className={cn(className, "!w-fit max-w-sm")}
      size="lg"
      closeOnSelect={closeOnSelect}
      drawerClasses={{
        "root&": "-top-screen w-full bottom-0 h-[100dvh]",
        ...drawerClasses,
      }}
      onChange={(option) => handleOnChangeLanguage(option)}
      title={title ?? t("language")}
      classes={classes}
    />
  );
};

export default LanguageSelect;
